import { type FC, useState } from 'react';

import { Icon } from '../Icon';
import {
  avatarBaseStyles,
  avatarFallbackStyles,
  avatarSizeStyles,
  avatarTextStyles,
} from './Avatar.styles';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
export type AvatarType = 'image' | 'text';

export type AvatarProps = {
  /**
   * Type of avatar - image or text initials
   */
  type?: AvatarType;
  /**
   * Source URL of the avatar image (required for type="image")
   */
  src?: string;
  /**
   * Text to generate initials from (required for type="text")
   */
  text?: string;
  /**
   * Alt text for the avatar
   */
  alt: string;
  /**
   * Size of the avatar
   */
  size?: AvatarSize;
  /**
   * Additional CSS class names
   */
  className?: string;
};

const Avatar: FC<AvatarProps> = ({
  type = 'image',
  src,
  text,
  alt,
  size = 'md',
  className,
}) => {
  const [hasImageError, setHasImageError] = useState(false);

  const handleImageError = () => {
    setHasImageError(true);
  };

  const getInitials = (fullName: string): string => {
    return fullName
      .split(' ')
      .map((name) => name.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('');
  };

  const getFontSize = (size: AvatarSize): string => {
    switch (size) {
      case 'xs':
        return '10px';
      case 'sm':
        return '12px';
      case 'md':
        return '16px';
      case 'lg':
        return '20px';
      case 'xl':
        return '36px';
      case '2xl':
        return '54px';
      default:
        return '16px';
    }
  };

  const getIconSize = (size: AvatarSize): import('../Icon/Icon').IconSize => {
    switch (size) {
      case 'xs':
        return 'xsmall';
      case 'sm':
        return 'small';
      case 'md':
        return 'large';
      case 'lg':
        return 'xlarge'; // Will need custom CSS for 28px
      case 'xl':
        return 'xxlarge';
      case '2xl':
        return 'xxxlarge';
      default:
        return 'xlarge';
    }
  };

  // Text avatar
  if (type === 'text' && text) {
    const initials = getInitials(text);
    return (
      <div
        css={[
          avatarTextStyles,
          avatarSizeStyles[size],
          { fontSize: getFontSize(size) },
        ]}
        className={className}
        title={alt}
      >
        {initials}
      </div>
    );
  }

  // Image avatar with error fallback
  if (type === 'image' && src && !hasImageError) {
    return (
      <img
        src={src}
        alt={alt}
        css={[avatarBaseStyles, avatarSizeStyles[size]]}
        className={className}
        onError={handleImageError}
      />
    );
  }

  // Fallback avatar (when image fails or no src provided)
  return (
    <div
      css={[avatarFallbackStyles, avatarSizeStyles[size]]}
      className={className}
      title={alt}
    >
      <Icon variant="User" size={getIconSize(size)} />
    </div>
  );
};

export default Avatar;
